import ApiService, { ApiResponse } from './ApiService';
import { PAGINATION_CONSTANTS } from '../constants/pagination';

export interface Client {
  id?: number;
  client_id?: number;
  org_id?: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
  is_deleted?: boolean;
  created_by?: {
    id: number;
    username: string;
  } | null;
  created_at?: string;
  updated_by?: {
    id: number;
    username: string;
  } | null;
  updated_at?: string;
}

export interface ClientListResponse {
  clients: Client[];
  total_count: number;
  page: number;
  page_size?: number;
  size?: number; // Keep for backward compatibility
  total_pages: number;
}

export interface ClientListParams {
  page?: number;
  size?: number;
  search?: string;
  org_id?: string;
  status?: string; // 'all' | 'active' | 'inactive'
  is_active?: boolean | string; // Keep for backward compatibility
}

export interface CreateClientRequest {
  client_id?: number;
  org_id?: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
}

export interface UpdateClientRequest extends CreateClientRequest {
  client_id: number;
}

class ClientService {
  private static instance: ClientService;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): ClientService {
    if (!ClientService.instance) {
      ClientService.instance = new ClientService();
    }
    return ClientService.instance;
  }

  public async getClients(params?: ClientListParams): Promise<ApiResponse<ClientListResponse>> {
    try {
      const response = await this.apiService.get<{
        is_success: boolean;
        status: number;
        message: string;
        data: [{
          clients: Client[];
        }];
      }>('/clients/list', undefined, this.apiService.getClientManagementUrl());

      if (response.success && response.data && response.data.is_success) {
        const clientData = response.data.data[0];
        const clients = clientData.clients || [];
        const clientListResponse: ClientListResponse = {
          clients: clients,
          total_count: clients.length,
          page: 1,
          size: clients.length,
          total_pages: 1
        };

        return {
          success: true,
          data: clientListResponse
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to fetch clients'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while fetching clients'
      };
    }
  }

  public async getClientsForAdmin(params?: ClientListParams): Promise<ApiResponse<ClientListResponse>> {
    try {
      // Build query parameters for the API
      let isactiveValue: string | boolean = 'all';
      if (params?.status) {
        // Convert status values to isactive values
        if (params.status === 'all') {
          isactiveValue = 'all';
        } else if (params.status === 'active') {
          isactiveValue = true;
        } else if (params.status === 'inactive') {
          isactiveValue = false;
        } else {
          isactiveValue = params.status;
        }
      } else if (params?.is_active !== undefined) {
        if (params.is_active === 'all') {
          isactiveValue = 'all';
        } else if (params.is_active === true || params.is_active === 'active') {
          isactiveValue = true;
        } else {
          isactiveValue = false;
        }
      }

      const queryParams: Record<string, any> = {
        search: params?.search || '',
        org_id: params?.org_id || '',
        is_active: isactiveValue,
        page: params?.page || 1,
        size: params?.size || 5,
      };

      const response = await this.apiService.get<{
        is_success: boolean;
        status: number;
        message: string;
        data: [{
          clients: Client[];
          total_count: number;
          page: number;
          page_size: number;
          total_pages: number;
        }];
      }>('/clients', queryParams, this.apiService.getClientManagementUrl());

      if (response.success && response.data && response.data.is_success) {
        const clientData = response.data.data[0];
        const clients = clientData.clients || [];
        // The pagination data is inside data[0], not at the root level
        const totalCount = clientData.total_count ?? 0;
        const pageSize = clientData.page_size ?? queryParams.size;
        const currentPage = clientData.page ?? queryParams.page;
        const totalPages = clientData.total_pages ?? 0;

        const clientListResponse: ClientListResponse = {
          clients: clients,
          total_count: totalCount,
          page: currentPage,
          page_size: pageSize,
          size: pageSize,
          total_pages: totalPages
        };

        return {
          success: true,
          data: clientListResponse
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to fetch clients'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while fetching clients'
      };
    }
  }

  public async createClient(clientData: CreateClientRequest): Promise<ApiResponse<Client>> {
    try {
      const response = await this.apiService.post<{
        is_success: boolean;
        status: number;
        message: string;
        data: Client;
      }>('/clients', clientData, this.apiService.getClientManagementUrl());

      if (response.success && response.data) {
        return {
          success: response.data.is_success,
          data: response.data.data,
          error: response.data.is_success ? undefined : response.data.message
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to create client'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while creating client'
      };
    }
  }

  public async updateClient(clientId: number, clientData: UpdateClientRequest): Promise<ApiResponse<Client>> {
    try {
      const response = await this.apiService.post<{
        is_success: boolean;
        status: number;
        message: string;
        data: Client;
      }>('/clients', clientData, this.apiService.getClientManagementUrl());

      if (response.success && response.data) {
        return {
          success: response.data.is_success,
          data: response.data.data,
          error: response.data.is_success ? undefined : response.data.message
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to update client'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while updating client'
      };
    }
  }

  public async deleteClient(clientId: number): Promise<ApiResponse<void>> {
    try {
      const response = await this.apiService.delete<{
        is_success: boolean;
        status: number;
        message: string;
      }>(`/clients/client_id=${clientId}`, undefined, this.apiService.getClientManagementUrl());

      if (response.success && response.data) {
        return {
          success: response.data.is_success,
          error: response.data.is_success ? undefined : response.data.message
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to delete client'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while deleting client'
      };
    }
  }

  public async getClientById(clientId: number): Promise<ApiResponse<Client>> {
    // For now, simulate API call
    const dummyClient: Client = {
      id: clientId,
      client_id: clientId,
      name: "Sample Client",
      email: "sample@client.com",
      phone: "+1-555-0000",
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    await new Promise(resolve => setTimeout(resolve, 500));

    return {
      success: true,
      data: dummyClient
    };

    // TODO: Uncomment when API is deployed
    // return this.apiService.get<Client>(`/clients/${clientId}`, undefined, this.apiService.getClientManagementUrl());
  }

  public async sendFtpCredentials(clientId: number): Promise<ApiResponse<string>> {
    try {
      const response = await this.apiService.post<{
        is_success: boolean;
        status: number;
        message: string;
        data: string;
      }>('/clients/send-credentials', { client_id: clientId }, this.apiService.getClientManagementUrl());

      if (response.success && response.data) {
        return {
          success: response.data.is_success,
          data: response.data.data,
          error: response.data.is_success ? undefined : response.data.message
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to send FTP credentials'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while sending FTP credentials'
      };
    }
  }

  public async resetFtpCredentials(clientId: number): Promise<ApiResponse<string>> {
    try {
      const response = await this.apiService.post<{
        is_success: boolean;
        status: number;
        message: string;
        data: string;
      }>('/clients/reset-credentials', { client_id: clientId }, this.apiService.getClientManagementUrl());

      if (response.success && response.data) {
        return {
          success: response.data.is_success,
          data: response.data.data,
          error: response.data.is_success ? undefined : response.data.message
        };
      }

      return {
        success: false,
        error: response.error || 'Failed to reset FTP credentials'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Network error occurred while resetting FTP credentials'
      };
    }
  }
}

export default ClientService;
